



import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-query';
import styled from 'styled-components';

import { loginService } from 'shared/services';
import { addUserInfo, handleAccessToken } from 'shared/slice/userSlice';
import { NOT_ALLOWED_ROLES } from 'shared/constants';

import { colors, fonts } from '../shared/tokens';
import { ArrowRightIcon, EyeIcon, EyeOffIcon } from '../shared/Icons';
import {
  Logo,
  AuthCard,
  CardTitle,
  CardSubtitle,
  FieldGroup,
  FieldLabel,
  InputShell,
  TextInput,
  TrailingButton,
  PrimaryButton,
  AuthorizedNotice,
  StatusFooter,
} from './authShared';

const AUTH_BG = '/images/redesign/auth-bg.jpg';

const PARAGRAPHS = [
  'UmbrellaOS is a centralized monitoring and control platform that gives operators real-time visibility over integrated track heating systems — providing status, condition reporting, and situational awareness across the entire rail network, even in the harshest winter conditions.',
  'Advanced dashboard analytics and video monitoring enable rapid validation of track conditions and operating practices, generating site-specific asset performance reports and reducing the crew and man-power needed to maintain switch locations and platforms.',
  'All operational performance history and telemetry is categorized, stored, and accessible at any time — viewable, analyzed, and exported for energy preservation guidelines and efficient operation practices.',
];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();


  const [notice] = useState(location.state?.notice || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const storedUsername = localStorage.getItem('rememberedUsername');
    if (storedUsername) {
      setUsername(storedUsername);
      setRemember(true);
    }
  }, []);

  useEffect(() => {
    if (remember) {
      localStorage.setItem('rememberedUsername', username);
    }
  }, [username, remember]);

  const { mutate: mutateLogin, isLoading } = useMutation(loginService, {
    onSuccess: (data) => {
      if (data?.token) {
        if (NOT_ALLOWED_ROLES.includes(data.user?.user_role)) {
          alert('You are not allowed to login');
          return;
        }
        localStorage.setItem('access_token', data.token);
        localStorage.setItem('loginTime', new Date());
        dispatch(handleAccessToken(data.token));
        dispatch(addUserInfo(data.user));
        navigate('/');
      }
    },
    onError: () => setErrorMessage('Invalid username or password'),
  });

  const handleRememberChange = (event) => {
    const checked = event.target.checked;
    setRemember(checked);
    if (checked) {
      localStorage.setItem('rememberedUsername', username);
    } else {
      localStorage.removeItem('rememberedUsername');
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    mutateLogin({ username, password, remember });
  };

  return (
    <Page>
      <Backdrop />

      <LeftPanel>
        <Logo big href="/" as="a" />

        <Copy>
          {PARAGRAPHS.map((text, index) => (
            <Paragraph key={index}>{text}</Paragraph>
          ))}
        </Copy>

        <BrandRow>
          <BrandInfo>
            <BrandKicker>Heating Solutions by</BrandKicker>
            <BrandName>TEMPORA®</BrandName>
            <BrandTagline>Track Transit &amp; Freight Heating Systems</BrandTagline>
          </BrandInfo>
          <LearnMore href="/" >
            Learn More
            <ArrowRightIcon size={12} />
          </LearnMore>
        </BrandRow>
      </LeftPanel>

      <RightPanel>
        <AuthCard as="form" onSubmit={handleSubmit}>
          <Logo />
          <CardTitle>Welcome Back</CardTitle>
          <CardSubtitle>Sign in to access your control platform</CardSubtitle>

          {notice && <NoticeText>{notice}</NoticeText>}

          <FieldGroup>
            <FieldLabel htmlFor="login-username">Username</FieldLabel>
            <InputShell>
              <TextInput
                id="login-username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setErrorMessage('');
                }}
                autoComplete="username"
              />
            </InputShell>
          </FieldGroup>

          <FieldGroup $gap={14}>
            <FieldLabel htmlFor="login-password">Password</FieldLabel>
            <InputShell>
              <TextInput
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrorMessage('');
                }}
                autoComplete="current-password"
                $hasTrailing
              />
              <TrailingButton
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
              </TrailingButton>
            </InputShell>
          </FieldGroup>

          <OptionsRow>
            <RememberLabel>
              <Checkbox
                type="checkbox"
                checked={remember}
                onChange={handleRememberChange}
              />
              Remember me
            </RememberLabel>
            <ForgotLink type="button" onClick={() => navigate('/reset-password')}>
              Forgot password?
            </ForgotLink>
          </OptionsRow>

          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

          <PrimaryButton type="submit" disabled={isLoading}>
            <ArrowRightIcon size={16} />
            <span>Enter Platform</span>
          </PrimaryButton>

          <AuthorizedNotice />
          <StatusFooter />
        </AuthCard>
      </RightPanel>
    </Page>
  );
};

export default Login;



const Page = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  background: ${colors.bg};
  overflow: hidden;
`;


const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${AUTH_BG});
  background-size: cover;
  background-position: center;
  z-index: 0;
`;

const LeftPanel = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 1 50%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 44px 60px 40px;

  background: linear-gradient(
      180deg,
      rgba(5, 8, 15, 0) 0%,
      rgba(5, 8, 15, 0) 55%,
      rgba(5, 8, 15, 0.95) 100%
    ),
    linear-gradient(
      106.8deg,
      rgba(5, 8, 15, 0.92) 7.7%,
      rgba(5, 8, 15, 0.72) 50%,
      rgba(5, 8, 15, 0.32) 92.3%
    );

  @media (max-width: 1024px) {
    display: none;
  }
`;

const Copy = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 440px;
  gap: 14px;
`;

const Paragraph = styled.p`
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 14px;
  line-height: 24.5px;
  color: #ffffff;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20.75px;
  border-top: 0.75px solid ${colors.hairline};
`;

const BrandInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const BrandKicker = styled.p`
  font-family: ${fonts.mono};
  font-weight: 400;
  font-size: 9px;
  line-height: 13.5px;
  letter-spacing: 1.08px;
  text-transform: uppercase;
  color: ${colors.muted2};
`;

const BrandName = styled.p`
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 22px;
  line-height: 24.2px;
  letter-spacing: 1.76px;
  text-transform: uppercase;
  color: ${colors.red};
`;

const BrandTagline = styled.p`
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 11px;
  line-height: 16.5px;
  letter-spacing: 0.44px;
  color: ${colors.muted2};
`;

const LearnMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8.75px 18.75px;
  border: 0.75px solid ${colors.redBorder};
  border-radius: 3px;
  color: ${colors.red};
  text-decoration: none;
  font-family: ${fonts.condensed};
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(224, 52, 52, 0.08);
    border-color: ${colors.red};
  }
`;

const RightPanel = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 1 50%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: linear-gradient(
    106.8deg,
    rgba(5, 8, 15, 0.32) 7.7%,
    rgba(5, 8, 15, 0.62) 41.5%,
    rgba(5, 8, 15, 0.78) 92.3%
  );

  @media (max-width: 1024px) {
    flex-basis: 100%;
    background: rgba(5, 8, 15, 0.6);
  }
`;



const OptionsRow = styled.div`
  width: 100%;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RememberLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: ${colors.muted};
  cursor: pointer;
`;

const Checkbox = styled.input`
  appearance: none;
  -webkit-appearance: none;
  width: 15.996px;
  height: 15.996px;
  margin: 0;
  border: 0.75px solid ${colors.checkboxBorder};
  border-radius: 3px;
  background: ${colors.inputBg};
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  &:checked {
    background: ${colors.cyan};
    border-color: ${colors.cyan};
  }
  &:checked::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 4px;
    height: 8px;
    border: solid ${colors.bg};
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }
`;

const ForgotLink = styled.button`
  font-family: ${fonts.mono};
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.48px;
  color: ${colors.muted};
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.cyan};
  }
`;

const ErrorText = styled.p`
  width: 100%;
  margin-top: 12px;
  font-family: ${fonts.body};
  font-size: 12px;
  color: ${colors.red};
  text-align: center;
`;

const NoticeText = styled.p`
  width: 100%;
  margin-top: 14px;
  font-family: ${fonts.body};
  font-size: 12px;
  color: ${colors.green};
  text-align: center;
`;
